import { AutoComplete, Box, DropdownLayoutValueOption, ListItemSection, ListItemSelect, Text } from '@wix/design-system'
import { useMetadata } from '@wix/funnel-builder-lizard'
import { HintSmall, Search } from '@wix/wix-ui-icons-common'
import { debounce } from 'debounce'
import React, { ChangeEventHandler, FC, useCallback, useEffect, useRef } from 'react'

import { usePageLogger } from '../../../../bi/mfi'
import { BusinessTermService } from '../../../reusable/Category/api/BusinessTermService'
import { loadCategories, selectCategory } from '../../../reusable/Category/state'
import { selectPopoverProps } from '../config/SelectPopoverProps'
import { useSecondTryContext } from '../SecondTryContext/SecondTryContext'

const debouncedLoadCategories = debounce(loadCategories, 400)

interface WidgetCategoryAutoCompleteProps {
  questionKey: string
  placeholder: string
  businessTermService: BusinessTermService
}

export const WidgetCategoryAutoComplete: FC<WidgetCategoryAutoCompleteProps> = ({
  questionKey,
  placeholder,
  businessTermService,
}) => {
  const { dispatch, categoryState } = useSecondTryContext()
  const { search, category, categoriesLoading, categories, searchSource } = categoryState
  const pageLogger = usePageLogger()
  const [{ language }] = useMetadata()
  const autoCompleteRef = useRef<AutoComplete>(null)

  const categoriesOptions: DropdownLayoutValueOption[] = categories.map(category => ({
    id: category.displayName || '',
    value: category.displayName || '',
  }))

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = e =>
    dispatch({ type: 'changeSearch', search: e.target.value })
  const handleClear = useCallback(() => {
    void pageLogger.generalAction({ action: 'delete_category' })
    dispatch({ type: 'changeSearch', search: '' })
  }, [pageLogger])
  const handleInputClick = useCallback(() => autoCompleteRef.current?.showOptions(), [])

  const handleSearchSelect = useCallback(
    async (option: DropdownLayoutValueOption) => {
      await selectCategory({
        dispatch,
        displayName: option.id as string,
        search,
        categories,
        pageLogger,
        questionKey,
        businessTermService,
      })
    },
    [categories, pageLogger, questionKey, search, businessTermService],
  )

  useEffect(() => {
    void debouncedLoadCategories({
      search,
      searchSource,
      dispatch,
      businessTermService,
      pageLogger,
      language,
    })
  }, [search, searchSource, dispatch, pageLogger, language, businessTermService])

  const prefixNode = (
    <Box height="100%" verticalAlign="middle">
      <Search color="#116DFF" />
    </Box>
  )

  return (
    <AutoComplete
      ref={autoCompleteRef}
      value={search || ''}
      selectedId={category?.businessTerm?.displayName}
      onChange={handleSearchChange}
      status={categoriesLoading ? 'loading' : undefined}
      options={categoriesOptions}
      placeholder={placeholder}
      onSelect={handleSearchSelect}
      onInputClicked={handleInputClick}
      maxHeightPixels="300"
      onClear={categoriesLoading ? undefined : handleClear}
      prefix={prefixNode}
      menuArrow={false}
      popoverProps={selectPopoverProps}
      size="small"
      fixedHeader={
        !categoriesLoading &&
        categories.length > 0 && (
          <>
            <ListItemSelect
              disabled
              title={
                <Box paddingTop="SP1">
                  <Text skin="disabled" size="tiny">
                    Editing your site type may remove some site apps. You can easily add them back.
                  </Text>
                </Box>
              }
              size="small"
              prefix={
                <Box>
                  <HintSmall size="18px" />
                </Box>
              }
            />
            <ListItemSection type="divider" />
          </>
        )
      }
    />
  )
}
